import { useState } from 'react'

import { attempt } from '~/api/attempt'
import { api } from '~/api/client'
import type { EventDetailPublic } from '~/api/types'

import s from './Decide.module.css'
import { Bookmark } from '~/components/Bookmark'

/**
 * Save this, and say you are going, from the gig's own page.
 *
 * The sheet had neither. Every card on the site carries both and the page the card opens
 * carried nothing — so the one screen with the whole bill, the tickets and the blurb on it,
 * which is the screen you are actually deciding on, was the one screen you could not decide
 * from. Reported as "still can't save an event when I'm on the event page", and "still"
 * because the last round of this put the heart on every card and never on the page.
 *
 * Labelled buttons rather than the card's corner marks. Those are icons that appear on hover
 * and the sheet is not a hover surface — the same reason the bill's follow star is drawn
 * without `.mini`'s `opacity: 0`. There is room here for the words, and a control you have
 * to discover is not what this page needs a second time.
 *
 * Going implies saved, which is the rule the calendar already follows: you cannot be going
 * to something that is not on your list, and un-going leaves it saved rather than removing
 * it, because changing your mind about attending is not changing your mind about the gig.
 */
export function Decide({ detail }: { detail: EventDetailPublic }) {
  const [liked, setLiked] = useState(Boolean(detail.event.liked))
  const [going, setGoing] = useState(Boolean(detail.event.going))

  // Nobody signed in. The server refuses both, and a control that refuses is a control that
  // should not have been drawn — the same rule the calendar's marks follow.
  if (detail.event.liked === undefined) return null

  const id = detail.event.id
  // A night that has already happened is not one you can be going to. The control still
  // works — you may well be recording that you went — but the tense has to follow, and the
  // page was saying "I am going" about a gig it was simultaneously drawing as over.
  const over = Boolean(detail.event.past)

  return (
    <div className={s.row}>
      <button
        type="button"
        className={s.mark}
        aria-pressed={liked}
        onClick={() => {
          const on = !liked
          setLiked(on)
          // Un-saving something you said you are going to would leave the two disagreeing,
          // and the going tick is the stronger statement, so it goes with it.
          if (!on) setGoing(false)
          // Patched first, written second, and put back if the write does not take. The
          // un-going that follows an un-save is sent only once the un-save took: side by
          // side, the second write's success cleared the first's sentence, so a refused
          // un-save reverted the heart in silence. A refused un-save puts both marks back
          // and sends nothing more; a refused un-going after it leaves the gig saved and
          // still going, which is what the server then has.
          void attempt(() => api.save(id, on)).then(async took => {
            if (!took) {
              setLiked(!on)
              if (!on && going) setGoing(true)
              return
            }
            if (!on && going && !(await attempt(() => api.going(id, false)))) setGoing(true)
          })
        }}
      >
        {/* Drawn, like every other mark on the site. This was `♥ Saved` and `♡ Save`, two
            text glyphs — the one place that typed what everywhere else draws, and the two
            characters are a different weight from each other in most faces. */}
        <Bookmark fill={liked} size={13} strokeWidth={1.8} />
        {liked ? 'Saved' : 'Save'}
      </button>

      <button
        type="button"
        className={`${s.mark} ${s.going}`}
        aria-pressed={going}
        onClick={() => {
          const on = !going
          setGoing(on)
          if (on) setLiked(true)
          // Only the tick goes back: going implied saved, and a refused going does not
          // un-save — the same rule the calendar's pages follow.
          void attempt(() => api.going(id, on)).then(took => {
            if (!took) setGoing(!on)
          })
        }}
      >
        {over ? (going ? '✓ I was there' : 'I was there') : going ? '✓ Going' : 'I am going'}
      </button>
    </div>
  )
}
